import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Popconfirm, message, Col, Select, Input, Row, Spin } from 'antd';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';

interface Product {
    _id: string;
    name: string;
    sku: string;
    stock: number;
    unit: string;
}

const InventoryList: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const fetchProducts = async () => {
        setLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/inventory`);
        setProducts(res.data);
        setLoading(false);
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    const handleDelete = async (id: string) => {
        await axios.delete(`${process.env.REACT_APP_API_URL}/inventory/${id}`);
        message.success('Product deleted');
        fetchProducts();
    };
    const [filters, setFilters] = useState({ name: '', category: '', product_type: '' });

    const filteredProducts = products.filter((p: any) =>
        p?.name?.toLowerCase()?.includes(filters.name?.toLowerCase()) &&
        (!filters.category || p.category === filters.category) &&
        (!filters.product_type || p.product_type === filters.product_type)
    );

    return (
        <AppLayout>
            <Spin spinning={loading}>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                        <Input
                            placeholder="Search by name"
                            value={filters.name}
                            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                        />
                    </Col>
                    <Col span={8}>
                        <Select
                            placeholder="Filter by category"
                            style={{ width: '100%' }}
                            allowClear
                            value={filters.category || undefined}
                            onChange={(val) => setFilters({ ...filters, category: val || '' })}
                            options={products.map((p: any) => p.category).filter(Boolean).map(c => ({ label: c, value: c }))}
                        />
                    </Col>
                    <Col span={8}>
                        <Select
                            placeholder="Filter by type"
                            style={{ width: '100%' }}
                            allowClear
                            value={filters.product_type || undefined}
                            onChange={(val) => setFilters({ ...filters, product_type: val || '' })}
                            options={[
                                { label: 'Stockable', value: 'stockable' },
                                { label: 'Consumable', value: 'consumable' },
                                { label: 'Service', value: 'service' },
                            ]}
                        />
                    </Col>
                </Row>

                <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="primary" onClick={() => navigate('/inventory/products/add-product')}>
                        Add Product
                    </Button>
                </Space>

                <Table
                    loading={loading}
                    dataSource={filteredProducts}
                    rowKey="id"
                    locale={{ emptyText: 'No products available. Please add a product to get started.' }}
                    columns={[
                        { title: 'Name', dataIndex: 'name' },
                        { title: 'SKU', dataIndex: 'sku' },
                        { title: 'Stock', dataIndex: 'stock' },
                        { title: 'Unit', dataIndex: 'unit' },
                        {
                            title: 'Actions',
                            render: (_, record) => (
                                <Space>
                                    <Button onClick={() => navigate(`/inventory/products/${record._id}`)}>Edit</Button>
                                    <Popconfirm
                                        title="Delete this product?"
                                        onConfirm={() => handleDelete(record._id)}
                                    >
                                        <Button danger>Delete</Button>
                                    </Popconfirm>
                                </Space>
                            )
                        }
                    ]}
                />
            </Spin>
        </AppLayout>
    );
};

export default InventoryList;
